// The Action mode's hex first-person walk model (docs/aod-studio-design.md
// §4.1-§4.2, phase A). Pure and deterministic: the FP view presents, the sim
// decides — `move_unit` intents remain the authoritative movement, this model
// only drives the camera and gates where the player may walk.
//
// Coordinates match render/HexGrid's `axialToWorld` (unit-size pointy-top
// hexes, odd-r axial): x = √3·(q + r/2), z = 1.5·r.

/** Biome movement costs — mirrors Rust `BiomeKind::movement_cost` exactly. */
export function movementCost(biome: string): number {
  return biome === 'forest' || biome === 'hills' || biome === 'river' ? 2 : 1;
}

/** Ocean and mountain block walking (the same impassable set as the board). */
export function isPassable(biome: string): boolean {
  return biome !== 'ocean' && biome !== 'mountain';
}

/** Axial → world XZ, unit-size pointy-top hexes. */
export function axialToWorld(q: number, r: number): [number, number] {
  return [Math.sqrt(3) * (q + r / 2), 1.5 * r];
}

/** World XZ → the axial hex containing the point (rounded). */
export function worldToAxial(x: number, z: number): { q: number; r: number } {
  const rf = z / 1.5;
  const qf = x / Math.sqrt(3) - rf / 2;
  return { q: Math.round(qf), r: Math.round(rf) };
}

export interface WalkState {
  /** World XZ position. */
  readonly x: number;
  readonly z: number;
  /** Heading in radians; 0 = −Z (into the screen). */
  readonly heading: number;
}

export type BiomeAt = (q: number, r: number) => string | undefined;

/** The biome under a world position; undefined = off the board (blocked). */
export function biomeAtWorld(
  doc: { tiles: readonly { q: number; r: number; biome: string }[] },
  x: number,
  z: number,
): string | undefined {
  const { q, r } = worldToAxial(x, z);
  return doc.tiles.find((t) => t.q === q && t.r === r)?.biome;
}

/**
 * One continuous walk step. Movement is scaled by the destination hex's
 * movement cost (forest feels heavier than plains — the *presentation* of
 * `BiomeKind::movement_cost`), and entering an impassable hex (ocean,
 * mountain, off-board) is clamped: the walk stops at the edge instead.
 * Heading turns with A/D independent of movement.
 */
export function applyWalk(
  state: WalkState,
  pressed: { forward: boolean; back: boolean; left: boolean; right: boolean },
  speed: number,
  dt: number,
  biomeAt: BiomeAt,
): WalkState {
  const turn = ((pressed.right ? 1 : 0) - (pressed.left ? 1 : 0)) * 2.4 * dt;
  const heading = state.heading + turn;
  const walk = ((pressed.forward ? 1 : 0) - (pressed.back ? 1 : 0)) * speed * dt;
  if (walk === 0) return { ...state, heading };
  const nx = state.x - Math.sin(heading) * walk;
  const nz = state.z - Math.cos(heading) * walk;
  const { q, r } = worldToAxial(nx, nz);
  const biome = biomeAt(q, r);
  if (biome === undefined || !isPassable(biome)) return { ...state, heading };
  const scale = 1 / movementCost(biome);
  return {
    x: state.x - Math.sin(heading) * walk * scale,
    z: state.z - Math.cos(heading) * walk * scale,
    heading,
  };
}
/**
 * The passable tile centre nearest a world point — the Action mode's spawn
 * rule: drop the camera at the orbit target's neighbourhood, never inside a
 * blocking tile (a corner ocean tile had the camera standing in the sea,
 * unable to walk anywhere).
 */
export function spawnNear(
  target: { readonly x: number; readonly z: number },
  tiles: readonly { props?: { q?: number; r?: number; biome?: string } }[],
): { x: number; z: number } | null {
  let best: { x: number; z: number; d: number } | null = null;
  for (const t of tiles) {
    const biome = t.props?.biome;
    if (biome === undefined || t.props?.q === undefined || t.props?.r === undefined) continue;
    if (!isPassable(biome)) continue;
    const [x, z] = axialToWorld(t.props.q, t.props.r);
    const d = Math.hypot(x - target.x, z - target.z);
    if (!best || d < best.d) best = { x, z, d };
  }
  return best ? { x: best.x, z: best.z } : null;
}

export interface StepInput {
  readonly forward: boolean;
  readonly back: boolean;
  readonly left: boolean;
  readonly right: boolean;
}

export interface WalkPosition {
  readonly x: number;
  readonly z: number;
  readonly moved: boolean;
}

/**
 * One continuous walk step in the LOOK direction (the mouse owns rotation;
 * WASD owns translation — W walks toward the look yaw, A/D strafe). Movement
 * is scaled by the destination hex's movement cost (forest feels heavier
 * than plains — the *presentation* of `movementCost`), and entering an
 * impassable hex (ocean, mountain, off-board) is clamped: the walk stops at
 * the edge instead.
 */
export function walkStep(
  state: { readonly x: number; readonly z: number },
  yaw: number,
  pressed: StepInput,
  speed: number,
  dt: number,
  biomeAt: BiomeAt,
): WalkPosition {
  const f = (pressed.forward ? 1 : 0) - (pressed.back ? 1 : 0);
  const s = (pressed.right ? 1 : 0) - (pressed.left ? 1 : 0);
  if (f === 0 && s === 0) return { x: state.x, z: state.z, moved: false };
  // Forward is -Z at yaw 0; right is +X.
  let dx = -Math.sin(yaw) * f + Math.cos(yaw) * s;
  let dz = -Math.cos(yaw) * f - Math.sin(yaw) * s;
  const len = Math.hypot(dx, dz) || 1;
  dx /= len;
  dz /= len;
  const step = speed * dt;
  const nx = state.x + dx * step;
  const nz = state.z + dz * step;
  const { q, r } = worldToAxial(nx, nz);
  const biome = biomeAt(q, r);
  if (biome === undefined || !isPassable(biome)) {
    return { x: state.x, z: state.z, moved: false };
  }
  const scale = 1 / movementCost(biome);
  return {
    x: state.x + dx * step * scale,
    z: state.z + dz * step * scale,
    moved: true,
  };
}
